import React, {
  KeyboardEvent,
  SyntheticEvent,
  useContext,
  useEffect,
  useRef
} from "react";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { useDispatch, useSelector } from "react-redux";
import { Loader, PolyIcon } from "elements";
import { hideSearch, showSearch } from "store/search";
import {
  DevicesListQuery,
  Filter,
  Maybe,
  useDevicesListQuery
} from "generated/graphql";
import {
  generatePath,
  Link,
  Link as SimpleLink,
  stringify,
  useHistory,
  useQueryArgs,
  useQueryParamString
} from "polyrouter";
import { State } from "store";
import { DEVICE_FIELD_TO_KEY, MAX_SEARCH_CHARS } from "Constants";
import { sanitizeSearchInput } from "utilities/search";
import _ from "lodash";
import "./SearchInput.scss";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

type SearchResult = DevicesListQuery["devices"];

interface SearchResultsProps {
  results: SearchResult;
  loading: boolean;
  search: string;
  count: number;
}

export const SearchResultsLoader: React.FC<{ search: string }> = ({
  search
}) => {
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const limitedSearch = search.substr(0, MAX_SEARCH_CHARS);
  const searchWords = limitedSearch.split(" ");
  const searchTerms: Filter[] = searchWords.map(word => ({ searchTerm: word }));

  const { data, fetching } = useDevicesListQuery({
    variables: {
      from: 0,
      filter: {
        AND: [
          {
            field: "tenantId",
            eq: tenantId
          },
          {
            AND: searchTerms
          }
        ]
      },
      limit: 10
    }
  });

  return (
    <SearchResults
      results={data?.devices || []}
      loading={fetching}
      search={limitedSearch}
      count={data?.countDevices || 0}
    />
  );
};

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  loading,
  search,
  count
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <div className="search-suggestions">
      <ul>
        <li>
          <SimpleLink
            className="search-header"
            onClick={() => dispatch(hideSearch())}
            to={{
              pathname: "/manage/inventory/table",
              search: stringify(
                {
                  searchTerm: search
                },
                {
                  parseNumbers: false
                }
              )
            }}
          >
            {t("DEVICES")}
            {loading ? null : (
              <span className="view-devices">
                {t("VIEW_ALL_DEVICES", { count })}
              </span>
            )}
          </SimpleLink>
          <Loader className="inline pt-1" loading={loading}>
            <ul>
              {results.map((device, index) => {
                let matchingField: {
                  field: LocalKeys;
                  value: Maybe<string>;
                } = {
                  field: "MAC_ADDRESS",
                  value: device.macAddress
                };

                Object.keys(device).forEach(field => {
                  let value = device[field as keyof SearchResult[number]];
                  if ((field === "room" || field === "site") && !!value) {
                    value = device[field]!["name"];
                  }
                  if (
                    value &&
                    typeof value === "string" &&
                    value.toLowerCase().includes(search.toLowerCase())
                  ) {
                    matchingField = {
                      field: DEVICE_FIELD_TO_KEY[field],
                      value: value
                    };
                  }
                });

                return (
                  <li key={`device-${index}`}>
                    <Link
                      data-testid="search-result"
                      to={`/manage/inventory/details/${device.id}`}
                      onClick={() => dispatch(hideSearch())}
                    >
                      <strong>{matchingField.value}</strong>
                      {matchingField.field !== "MAC_ADDRESS" && (
                        <span> - {device.macAddress}</span>
                      )}
                      <p className="matching-field">{t(matchingField.field)}</p>
                    </Link>
                  </li>
                );
              })}
              {results.length === 0 ? (
                <li>
                  <span className="search-empty">{t("NO_RESULTS")}</span>
                </li>
              ) : null}
            </ul>
          </Loader>
        </li>
      </ul>
    </div>
  );
};

export type SearchQuery = "search";

export const SearchInput: React.FC<{ debounce?: number }> = ({
  debounce = 500
}) => {
  const { t } = useTranslation();
  const [urlSearchTerm, updateURLSearch, clearURLSearch] = useQueryParamString<
    SearchQuery
  >("search");
  const dispatch = useDispatch();
  const history = useHistory();
  const iconRef = React.useRef<HTMLSpanElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const searchRef: React.RefObject<HTMLLabelElement> = useRef(null);
  const searchOpen = useSelector((state: State) => state.search.open);
  const searchLabel = t("SEARCH_DEVICES");
  const queryParams = useQueryArgs();

  const updateURLRef = useRef(
    _.debounce((search: string) => updateURLSearch(search), debounce)
  );
  const updateDebouncedURL = updateURLRef.current;

  const closeSearch = () => {
    clearURLSearch();
    dispatch(hideSearch());
  };

  const handleSearchToggle = () => {
    // dismiss search if clicking icon while search open
    if (searchOpen) {
      closeSearch();
      return;
    }

    inputRef.current!.focus();
    dispatch(showSearch());
  };

  const handleClick = (e: MouseEvent) => {
    // handle icon clicks separately, so ignore them here
    if (iconRef.current && iconRef.current.contains(e.target as Node)) {
      return;
    }

    // dismiss search if clicking anywhere else in document
    if (
      searchOpen &&
      (!searchRef.current || !searchRef.current.contains(e.target as Node))
    ) {
      closeSearch();
    }
  };

  const handleKeyPress = (event: SyntheticEvent) => {
    const key = (event as KeyboardEvent).key;

    if (key === "Enter" && urlSearchTerm) {
      const search = stringify(
        {
          ...queryParams,
          searchTerm: urlSearchTerm?.trim() || ""
        },
        {
          parseNumbers: false
        }
      );
      history.push(generatePath("/manage/inventory/table?:search", { search }));
    }
  };

  useEffect(() => {
    updateURLRef.current = _.debounce(
      (search: string) => updateURLSearch(search),
      debounce
    );
  }, [queryParams, updateURLRef, updateURLSearch, debounce]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClick);
    if (urlSearchTerm) {
      inputRef.current!.focus();
      dispatch(showSearch());

      if (urlSearchTerm.length > MAX_SEARCH_CHARS) {
        updateURLSearch(urlSearchTerm.substr(0, MAX_SEARCH_CHARS));
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  });

  return (
    <>
      <span
        data-testid="search-icon"
        title={t("OPEN_SEARCH")}
        className={`icon-wrap nd-1  ${searchOpen ? "active" : ""}`}
        ref={iconRef}
      >
        <PolyIcon
          icon="search"
          className="white main-icon"
          onClick={handleSearchToggle}
        />
      </span>
      <label
        ref={searchRef}
        className={`search-input ${searchOpen ? "open" : ""}`}
      >
        <div>
          <span className="sr-only">{searchLabel}</span>
          <PolyIcon className="white" icon="search" />
          <input
            ref={inputRef}
            onFocus={() =>
              (inputRef.current!.value = urlSearchTerm ? urlSearchTerm : "")
            }
            data-testid="search-input-field"
            aria-label={searchLabel}
            className="search-field"
            placeholder={searchLabel}
            onKeyDown={handleKeyPress}
            onChange={e =>
              updateDebouncedURL(sanitizeSearchInput(e.target.value))
            }
            maxLength={MAX_SEARCH_CHARS}
          />
          <span
            data-testid="search-clear"
            title="search-clear"
            className={
              "clear-wrap search-" + (urlSearchTerm ? "full" : "empty")
            }
            onClick={() => clearURLSearch()}
          >
            <PolyIcon className="white" icon="clear_x" />
          </span>
          {urlSearchTerm && urlSearchTerm.trim().length > 2 && (
            <SearchResultsLoader search={urlSearchTerm.trim()} />
          )}
        </div>
      </label>
    </>
  );
};
